import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Hotspot } from '../types';
import { applications } from '../data/applications';

interface HotspotLegendProps {
  hotspots: Hotspot[];
  onHotspotSelect?: (hotspot: Hotspot) => void;
  selectedHotspot?: Hotspot | null;
}

const HotspotLegend: React.FC<HotspotLegendProps> = ({ 
  hotspots, 
  onHotspotSelect, 
  selectedHotspot 
}) => {
  const [activeCategory, setActiveCategory] = useState<string>('all');

  // Get General category applications from applications.ts
  const generalApplications = useMemo(() => {
    return applications
      .filter(app => app.category === 'General')
      .map(app => ({
        id: app.id,
        x: 0,
        y: 0,
        title: app.title,
        description: app.description,
        issue: app.problem || '',
        solution: app.solution || '',
        productLink: app.relatedProducts[0] ? `/products/${app.relatedProducts[0]}` : '/products/applying',
        videoLink: app.videoUrl || '/videos/general'
      }));
  }, []);

  // Merge General applications with hotspots
  const generalItems = useMemo(() => {
    const waterproofFlashingHotspot = hotspots.find(h => h.id === 'waterproof-flashing');
    const crackRepairHotspot = hotspots.find(h => h.id === 'crack-repair');
    // Get all General applications (waterproof-flashing and crack-repair)
    const items: any[] = [];
    
    generalApplications.forEach(app => {
      if (app.id === 'waterproof-flashing' && waterproofFlashingHotspot) {
        // Use the actual waterproof-flashing hotspot
        items.push({
          ...waterproofFlashingHotspot,
          id: 'waterproof-flashing',
          title: app.title,
          description: app.description,
          issue: app.issue,
          solution: app.solution
        });
      } else if (app.id === 'crack-repair' && crackRepairHotspot) {
        // Use the actual crack-repair hotspot
        items.push({
          ...crackRepairHotspot,
          id: 'crack-repair',
          title: app.title,
          description: app.description,
          issue: app.issue,
          solution: app.solution
        });
      }
    });
    
    return items;
  }, [hotspots, generalApplications]);

  // Create combined list for "All Applications" - includes all hotspots
  const allApplications = useMemo(() => {
    // All hotspots are already in the array, including waterproof-flashing and crack-repair
    // Just update the General category items with application data
    const allItems = [...hotspots];
    
    // Update waterproof-flashing hotspot with application data if it exists
    const waterproofIndex = allItems.findIndex(h => h.id === 'waterproof-flashing');
    if (waterproofIndex !== -1) {
      const waterproofApp = generalApplications.find(a => a.id === 'waterproof-flashing');
      if (waterproofApp) {
        allItems[waterproofIndex] = {
          ...allItems[waterproofIndex],
          title: waterproofApp.title,
          description: waterproofApp.description,
          issue: waterproofApp.issue,
          solution: waterproofApp.solution
        };
      }
    }
    
    // Update crack-repair hotspot with application data if it exists
    const crackIndex = allItems.findIndex(h => h.id === 'crack-repair');
    if (crackIndex !== -1) {
      const crackApp = generalApplications.find(a => a.id === 'crack-repair');
      if (crackApp) {
        allItems[crackIndex] = {
          ...allItems[crackIndex],
          title: crackApp.title,
          description: crackApp.description,
          issue: crackApp.issue,
          solution: crackApp.solution
        };
      }
    }
    
    return allItems;
  }, [hotspots, generalApplications]);

  // Categorize hotspots
  const categories = {
    'all': { label: 'All Applications', color: 'gray', hotspots: allApplications },
    'below-grade': { 
      label: 'Below Grade / Underground', 
      color: 'blue', 
      hotspots: hotspots.filter(h => ['basement', 'foundation-walls'].includes(h.id))
    },
    'above-grade': { 
      label: 'Above Grade', 
      color: 'green', 
      hotspots: hotspots.filter(h => ['chimney-flashing', 'roof-applications', 'pitched-roof', 'flat-roof', 'facades', 'balcony', 'decks-terraces', 'retaining-walls', 'planters', 'bathroom'].includes(h.id))
    },
    'submerged': { 
      label: 'Submerged Applications', 
      color: 'purple', 
      hotspots: hotspots.filter(h => ['swimming-pool', 'spas', 'cisterns-tanks', 'fountain'].includes(h.id))
    },
    'general': { 
      label: 'General', 
      color: 'yellow', 
      hotspots: generalItems
    }
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      gray: 'bg-gray-100 text-gray-800 border-gray-300',
      blue: 'bg-blue-100 text-blue-800 border-blue-300',
      green: 'bg-green-100 text-green-800 border-green-300',
      purple: 'bg-purple-100 text-purple-800 border-purple-300',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300'
    };
    return colors[category as keyof typeof colors] || colors.gray;
  };

  const getHotspotColor = (hotspot: Hotspot) => {
    if (['basement', 'foundation-walls'].includes(hotspot.id)) {
      return 'bg-blue-500';
    } else if (['swimming-pool', 'spas', 'cisterns-tanks', 'fountain'].includes(hotspot.id)) {
      return 'bg-purple-500';
    } else if (['waterproof-flashing', 'crack-repair'].includes(hotspot.id)) {
      return 'bg-yellow-500';
    } else {
      return 'bg-green-500';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-xl font-bold text-gray-900 mb-6">
        🎯 Flexcrete Applications ({allApplications.length} Total)
      </h3>

      {/* Category Filter */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {Object.entries(categories).map(([key, category]) => (
            <button
              key={key}
              onClick={() => setActiveCategory(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeCategory === key
                  ? `${getCategoryColor(category.color)} border-2`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {category.label} ({category.hotspots.length})
            </button>
          ))}
        </div>
      </div>

      {/* Hotspots Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories[activeCategory as keyof typeof categories].hotspots.map((hotspot, index) => (
          <motion.div
            key={hotspot.id}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              selectedHotspot?.id === hotspot.id
                ? 'border-primary-500 bg-primary-50 shadow-md'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
            }`}
            onClick={() => onHotspotSelect?.(hotspot)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-start gap-3">
              <div className={`w-4 h-4 rounded-full ${getHotspotColor(hotspot)} mt-1 flex-shrink-0`}></div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 text-sm mb-1">
                  {hotspot.title}
                </h4>
                <p className="text-xs text-gray-600 line-clamp-2">
                  {hotspot.description}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    Position: {hotspot.x}%, {hotspot.y}%
                  </span>
                  {selectedHotspot?.id === hotspot.id && (
                    <span className="text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                      Selected
                    </span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {categories['below-grade'].hotspots.length}
            </div>
            <div className="text-xs text-blue-800">Below Grade</div>
          </div>
          <div className="bg-green-50 p-3 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {categories['above-grade'].hotspots.length}
            </div>
            <div className="text-xs text-green-800">Above Grade</div>
          </div>
          <div className="bg-purple-50 p-3 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {categories['submerged'].hotspots.length}
            </div>
            <div className="text-xs text-purple-800">Submerged</div>
          </div>
          <div className="bg-yellow-50 p-3 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              {categories['general'].hotspots.length}
            </div>
            <div className="text-xs text-yellow-800">General</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotspotLegend;
